/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { ViewChild, Component, Input, Output, EventEmitter, ElementRef, Optional, ViewEncapsulation, ChangeDetectionStrategy, } from '@angular/core';
import { trigger, state, style, animate, transition, } from '@angular/animations';
import { PortalHostDirective } from '@angular/cdk/portal';
import { Directionality } from '@angular/cdk/bidi';
/**
 * Wrapper for the contents of a tab.
 * \@docs-private
 */
var MatTabBody = (function () {
    /**
     * @param {?} _elementRef
     * @param {?} _dir
     */
    function MatTabBody(_elementRef, _dir) {
        this._elementRef = _elementRef;
        this._dir = _dir;
        /**
         * Event emitted when the tab begins to animate towards the center as the active tab.
         */
        this._onCentering = new EventEmitter();
        /**
         * Event emitted when the tab completes its animation towards the center.
         */
        this._onCentered = new EventEmitter(true);
    }
    Object.defineProperty(MatTabBody.prototype, "position", {
        /**
         * @param {?} position
         * @return {?}
         */
        set: function (position) {
            if (position < 0) {
                this._position = this._getLayoutDirection() == 'ltr' ? 'left' : 'right';
            }
            else if (position > 0) {
                this._position = this._getLayoutDirection() == 'ltr' ? 'right' : 'left';
            }
            else {
                this._position = 'center';
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MatTabBody.prototype, "origin", {
        /**
         * The origin position from which this tab should appear when it is centered into view.
         * @param {?} origin
         * @return {?}
         */
        set: function (origin) {
            if (origin == null) {
                return;
            }
            var /** @type {?} */ dir = this._getLayoutDirection();
            if ((dir == 'ltr' && origin <= 0) || (dir == 'rtl' && origin > 0)) {
                this._origin = 'left';
            }
            else {
                this._origin = 'right';
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * After initialized, check if the content is centered and has an origin. If so, set the
     * special position states that transition the tab from the left or right before centering.
     * @return {?}
     */
    MatTabBody.prototype.ngOnInit = function () {
        if (this._position == 'center' && this._origin) {
            this._position = this._origin == 'left' ? 'left-origin-center' : 'right-origin-center';
        }
    };
    /**
     * After the view has been set, check if the tab content is set to the center and attach the
     * content if it is not already attached.
     * @return {?}
     */
    MatTabBody.prototype.ngAfterViewChecked = function () {
        if (this._isCenterPosition(this._position) && !this._portalHost.hasAttached()) {
            this._portalHost.attach(this._content);
        }
    };
    /**
     * @param {?} e
     * @return {?}
     */
    MatTabBody.prototype._onTranslateTabStarted = function (e) {
        if (this._isCenterPosition(e.toState)) {
            this._onCentering.emit(this._elementRef.nativeElement.clientHeight);
        }
    };
    /**
     * @param {?} e
     * @return {?}
     */
    MatTabBody.prototype._onTranslateTabComplete = function (e) {
        // If the end state is that the tab is not centered, then detach the content.
        if (!this._isCenterPosition(e.toState) && !this._isCenterPosition(this._position)) {
            this._portalHost.detach();
        }
        // If the transition to the center is complete, emit an event.
        if (this._isCenterPosition(e.toState) && this._isCenterPosition(this._position)) {
            this._onCentered.emit();
        }
    };
    /**
     * The text direction of the containing app.
     * @return {?}
     */
    MatTabBody.prototype._getLayoutDirection = function () {
        return this._dir && this._dir.value === 'rtl' ? 'rtl' : 'ltr';
    };
    /**
     * Whether the provided position state is considered center, regardless of origin.
     * @param {?} position
     * @return {?}
     */
    MatTabBody.prototype._isCenterPosition = function (position) {
        return position == 'center' ||
            position == 'left-origin-center' ||
            position == 'right-origin-center';
    };
    MatTabBody.decorators = [
        { type: Component, args: [{selector: 'mat-tab-body',
                    template: "<div class=\"mat-tab-body-content\" #content [@translateTab]=\"_position\" (@translateTab.start)=\"_onTranslateTabStarted($event)\" (@translateTab.done)=\"_onTranslateTabComplete($event)\"><ng-template cdkPortalHost></ng-template></div>",
                    styles: [".mat-tab-body-content{height:100%;overflow:auto}.mat-tab-group-dynamic-height .mat-tab-body-content{overflow:hidden}"],
                    encapsulation: ViewEncapsulation.None,
                    preserveWhitespaces: false,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    host: {
                        'class': 'mat-tab-body',
                    },
                    animations: [
                        trigger('translateTab', [
                            // Note: transitions to `none` instead of 0, because some browsers might blur the content.
                            state('center, void, left-origin-center, right-origin-center', style({ transform: 'none' })),
                            state('left', style({ transform: 'translate3d(-100%, 0, 0)' })),
                            state('right', style({ transform: 'translate3d(100%, 0, 0)' })),
                            transition('* => left, * => right, left => center, right => center', animate('500ms cubic-bezier(0.35, 0, 0.25, 1)')),
                            transition('void => left-origin-center', [
                                style({ transform: 'translate3d(-100%, 0, 0)' }),
                                animate('500ms cubic-bezier(0.35, 0, 0.25, 1)')
                            ]),
                            transition('void => right-origin-center', [
                                style({ transform: 'translate3d(100%, 0, 0)' }),
                                animate('500ms cubic-bezier(0.35, 0, 0.25, 1)')
                            ])
                        ])
                    ]
                },] },
    ];
    /**
     * @nocollapse
     */
    MatTabBody.ctorParameters = function () { return [
        { type: ElementRef, },
        { type: Directionality, decorators: [{ type: Optional },] },
    ]; };
    MatTabBody.propDecorators = {
        '_portalHost': [{ type: ViewChild, args: [PortalHostDirective,] },],
        '_onCentering': [{ type: Output },],
        '_onCentered': [{ type: Output },],
        '_content': [{ type: Input, args: ['content',] },],
        'position': [{ type: Input, args: ['position',] },],
        'origin': [{ type: Input, args: ['origin',] },],
    };
    return MatTabBody;
}());
export { MatTabBody };
function MatTabBody_tsickle_Closure_declarations() {
    /** @type {?} */
    MatTabBody.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatTabBody.ctorParameters;
    /** @type {?} */
    MatTabBody.propDecorators;
    /**
     * The portal host inside of this container into which the tab body content will be loaded.
     * @type {?}
     */
    MatTabBody.prototype._portalHost;
    /**
     * Event emitted when the tab begins to animate towards the center as the active tab.
     * @type {?}
     */
    MatTabBody.prototype._onCentering;
    /**
     * Event emitted when the tab completes its animation towards the center.
     * @type {?}
     */
    MatTabBody.prototype._onCentered;
    /**
     * The tab body content to display.
     * @type {?}
     */
    MatTabBody.prototype._content;
    /**
     * The shifted index position of the tab body, where zero represents the active center tab.
     * @type {?}
     */
    MatTabBody.prototype._position;
    /**
     * The origin position from which this tab should appear when it is centered into view.
     * @type {?}
     */
    MatTabBody.prototype._origin;
    /** @type {?} */
    MatTabBody.prototype._elementRef;
    /** @type {?} */
    MatTabBody.prototype._dir;
}
//# sourceMappingURL=tab-body.js.map